import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Search as SearchIcon,
} from "lucide-react";
import EventCard from "./EventCard";
import { useListStageCards } from "../../../../../services/stagemode_api/talent/talent_api";

interface FeaturedEventCardTNProps {
  fromRoutes?: boolean;
}

const FeaturedEventCardTN: React.FC<FeaturedEventCardTNProps> = ({
  fromRoutes = false,
}) => {
  const { data } = useListStageCards();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const events = data?.data ?? [];
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollBy = (offset: number) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  const MAX_VISIBLE = 20;

  const filteredEvents = events.filter((event: any) =>
    event.title?.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const visibleEvents = showAll
    ? filteredEvents
    : filteredEvents.slice(0, MAX_VISIBLE);

  // —— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.75)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
  };

  if (!events.length) {
    return (
      <Box
        px={{ xs: 2, sm: 3, md: 4 }}
        py={6}
        textAlign="center"
        sx={{
          background: COLORS.bg,
          color: COLORS.text,
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            px: 3,
            py: 2,
            borderRadius: 2,
            backgroundColor: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
          }}
        >
          <Typography
            variant="h6"
            fontFamily="Lexend"
            fontWeight={800}
            mb={1}
            sx={{ color: COLORS.text }}
          >
            No Featured Events
          </Typography>
          <Typography
            fontSize={14}
            fontFamily="Lexend"
            sx={{ color: COLORS.muted }}
          >
            Check back later for exciting events curated just for you.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      px={{ xs: 2, sm: 3, md: 5 }}
      py={{ xs: 5, sm: 6 }}
      sx={{
        background: COLORS.bg,
        color: COLORS.text,
        borderRadius: 3,
        position: "relative",
        // subtle vignette + glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1000px 420px at 8% 0%, rgba(229,9,20,.08), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* Header */}
      {!fromRoutes && (
        <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            mb={3}
            spacing={2}
          >
            <Typography
              fontFamily="Lexend"
              fontWeight={900}
              fontSize={{ xs: 20, sm: 24, md: 26 }}
              sx={{
                color: COLORS.text,
                letterSpacing: "-0.01em",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                "&::after": {
                  content: '""',
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: COLORS.red,
                  boxShadow: "0 0 12px rgba(229,9,20,0.6)",
                },
              }}
            >
              🌟 Stage Events
            </Typography>

            <Box
              sx={{
                display: "flex",
                direction: "row",
                gap: 2,
                justifyContent: { xs: "space-between", md: "center" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              {/* Search Bar (dark) */}
              <Box>
                <TextField
                  placeholder="Search by title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 280 },
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: COLORS.text,
                      borderRadius: 2,
                      height: 40,
                      border: `1px solid ${COLORS.border}`,
                      transition: "border-color .2s",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: COLORS.border },
                      "&.Mui-focused fieldset": { borderColor: COLORS.red },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "rgba(255,255,255,0.6)",
                      opacity: 1,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ color: "rgba(255,255,255,0.6)" }}
                      >
                        <SearchIcon size={18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Scroll controls */}
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                <IconButton
                  onClick={() => scrollBy(-400)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    backdropFilter: "blur(6px)",
                    transition: "all .2s ease",
                    color: COLORS.text,
                    "&:hover": {
                      backgroundColor: COLORS.red,
                      borderColor: COLORS.red,
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
                    },
                  }}
                >
                  <ChevronLeftIcon size={16} />
                </IconButton>

                <IconButton
                  onClick={() => scrollBy(400)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    backdropFilter: "blur(6px)",
                    transition: "all .2s ease",
                    color: COLORS.text,
                    "&:hover": {
                      backgroundColor: COLORS.red,
                      borderColor: COLORS.red,
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
                    },
                  }}
                >
                  <ChevronRightIcon size={16} />
                </IconButton>
              </Box>
            </Box>
          </Stack>

          <Divider
            sx={{
              borderColor: COLORS.border,
              mb: 3,
              display: { xs: "none", sm: "block" },
            }}
          />
        </>
      )}

      {/* Carousel + edge fades */}
      <Box sx={{ width: "100%", position: "relative", overflow: "hidden" }}>
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: { xs: 16, sm: 24, md: 32 },
            background:
              "linear-gradient(90deg, rgba(11,11,11,1), rgba(11,11,11,0))",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: { xs: 16, sm: 24, md: 32 },
            background:
              "linear-gradient(270deg, rgba(11,11,11,1), rgba(11,11,11,0))",
            zIndex: 1,
          }}
        />

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            overflowX: "auto",
            overflowY: "hidden",
            pb: 1,
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {visibleEvents.map((event: any) => (
            <Grid item key={event.id} xs={12} sm={6} md={4} lg={3} xl={2.4}>
              <Box
                onClick={() => navigate(`/featured-event/${event.id}`)}
                sx={{
                  mt: 2,
                  pb: 4,
                  maxWidth: { xs: 160, sm: 250 },
                  minWidth: { xs: 160, sm: 250 },
                  width: "100%",
                  cursor: "pointer",
                  transition: "transform .3s ease, box-shadow .3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                  },
                }}
              >
                <EventCard event={event} />
              </Box>
            </Grid>
          ))}
        </Box>
      </Box>

      {/* (Optional) View All button kept commented to preserve original behavior */}
      {/* {filteredEvents.length > MAX_VISIBLE && (
        <Box mt={6} textAlign="center">
          <Button
            onClick={() => setShowAll((prev) => !prev)}
            variant="contained"
            sx={{
              px: 4,
              py: 1.5,
              fontFamily: "Lexend",
              fontWeight: 800,
              fontSize: 14,
              borderRadius: 2,
              backgroundColor: COLORS.red,
              color: "#fff",
              textTransform: "none",
              boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
              "&:hover": { backgroundColor: COLORS.redHover },
            }}
          >
            {showAll ? "Show Less" : `View All (${filteredEvents.length})`}
          </Button>
        </Box>
      )} */}
    </Box>
  );
};

export default FeaturedEventCardTN;
